import React, { useRef } from 'react';
import type { Id, ToastContent, ToastOptions } from 'react-toastify';
import { toast, ToastContainer, Zoom } from 'react-toastify';
import { atom, useAtom, useAtomValue } from 'jotai';
import { Box } from '../ui';
import useEvent from '../hooks/useEvent';
import type { DefinedFields } from '../../lib/util';

const secondaryToastIdsAtom = atom(new Set<Id>());
const toastContainerZIndexAtom = atom(10000);

/**
 * Custom hook that provides the current z-index value for the ToastContainer and a function to update it.
 * The z-index value is stored in a jotai atom, which allows it to be shared and updated across different components.
 */
export const ToastContainerZIndex = () => {
  const [zIndex, setZIndex] = useAtom(toastContainerZIndexAtom);
  return { zIndex, setZIndex };
};

/** Ensures that if toastId is always given, if not as default then in toast options */
type Options<DefaultIdType> = DefaultIdType extends undefined
  ? DefinedFields<ToastOptions, 'toastId'>
  : ToastOptions | undefined;

/**
 * Custom hook for displaying toast notifications.
 */
export function useNotify<
  DefaultId extends ToastOptions['toastId'] | undefined = undefined,
>(defaults?: {
  toastId?: DefaultId;
  type?: ToastOptions['type'];
  autoClose?: ToastOptions['autoClose'];
  dismissible?: boolean;
}) {
  const ids = useAtomValue(secondaryToastIdsAtom);
  const defaultToastId = useRef<ToastOptions['toastId']>(defaults?.toastId);

  return useEvent(
    (
      message: ToastContent,
      options?: Options<DefaultId> & {
        secondaryId?: string;
        updateOnly?: boolean;
      },
    ) => {
      const toastId = options?.toastId ?? defaultToastId.current;
      const secondaryId = options?.secondaryId;
      const type = options?.type ?? defaults?.type ?? 'error';
      const autoClose = options?.autoClose ?? defaults?.autoClose;

      if (toastId === undefined) throw Error('Missing toastId');

      if (secondaryId && ids.has(secondaryId)) return;
      if (secondaryId) {
        ids.add(secondaryId);
      }

      if (!toast.isActive(toastId)) {
        if (options?.updateOnly) {
          return;
        }
        toast(message, {
          ...options,
          toastId,
          type,
          autoClose,
          data: { dismissible: defaults?.dismissible },
        });
      } else {
        toast.update(toastId, {
          ...options,
          render: message,
          toastId,
          type,
          autoClose,
        });
      }
    },
  );
}

export type Notify = ReturnType<typeof useNotify>;

/**
 *  Closes all the open notifications
 */
export function useCloseToasts() {
  return useEvent(() => {
    toast.dismiss();
  });
}

/**
 * Renders the react-toastify notifications
 */
function ToastNotifications() {
  const { zIndex } = ToastContainerZIndex();
  return (
    <Box position="fixed" width="100%" zIndex={zIndex}>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        newestOnTop={false}
        closeOnClick
        draggable
        transition={Zoom}
        theme="colored"
      />
    </Box>
  );
}

export default ToastNotifications;
